// `ConnectFlow` — Settings-side connect surface (Wave B7).
//
// Two pairing methods, surfaced by a method picker at the top:
//   - **Extension (default)** — mints a pairing code via `usePairExtension`,
//     renders it for the user to paste into the Barel Chrome extension popup,
//     and listens for the `account.connected` UserHub event.
//   - **Cookie (li_at)** — paste-then-validate-then-confirm, fully owned by
//     `<CookieConnectMethod />`. The extension is not involved.
//
// Reconnect mode (`?reconnect=<accountId>`):
//   - Method picker hidden — extension method is forced.
//   - Title swapped to "Reconnecter le compte {name}".
//   - The extension method runs as today (the SW will recognise the URN
//     and unarchive the existing row server-side).

import {
  ChevronLeft,
  Copy,
  ExternalLink,
  Lock,
  Puzzle,
  RefreshCw,
  ShieldCheck,
} from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';

import {
  ApiError,
  useLinkedinAccounts,
  usePairExtension,
  useUserHubChannel,
  type LinkedinAccountRow,
  type PairExtensionResponse,
  type UserHubServerEvent,
} from '@barel/api-client/react';
import { useT } from '@barel/i18n';
import { Banner, Button, Card, Segmented } from '@barel/ui';

import {
  BAREL_EXTENSION_ID,
  BAREL_EXTENSION_INSTALL_URL,
  HAS_PUBLISHED_EXTENSION,
} from '@/lib/extensionConfig';

import { CookieConnectMethod } from './CookieConnectMethod';
import { useExtensionPresence } from './lib/useExtensionPresence';

export interface ConnectFlowProps {
  /** Optional Chrome extension id used for the presence probe. Defaults
   *  to the build-time `BAREL_EXTENSION_ID` so callers don't usually pass
   *  this — the prop is mostly here for unit tests. */
  extensionId?: string;
  /** Called after the extension acks the pair OR cookie commit succeeds. */
  onConnected: () => void;
  /** Called when the user clicks the back button. */
  onBack: () => void;
  /** Test-only — pre-fill the reconnect search param without TanStack. */
  reconnectIdOverride?: string;
}

interface PairingCodeState extends PairExtensionResponse {
  expiresAt: number;
}

type CopyState = 'idle' | 'copied' | 'failed';
type Method = 'extension' | 'cookie';

/**
 * Read `?reconnect=<id>` straight from `window.location.search`.
 *
 * We deliberately avoid `useSearch()` from TanStack Router — the existing
 * unit tests render `ConnectFlow` outside any RouterProvider, and TanStack's
 * hooks throw an invariant in that case. The query string is the source of
 * truth either way; reading it directly keeps the component testable in
 * isolation while still working under the real Router shell.
 */
function readReconnectSearchParam(): string | undefined {
  if (typeof window === 'undefined') return undefined;
  const params = new URLSearchParams(window.location.search);
  const value = params.get('reconnect');
  return value ?? undefined;
}

export function ConnectFlow({
  extensionId = BAREL_EXTENSION_ID,
  onConnected,
  onBack,
  reconnectIdOverride,
}: ConnectFlowProps): ReactNode {
  const t = useT('accounts');
  const presence = useExtensionPresence(extensionId);
  const pairExtension = usePairExtension();

  const reconnectId = reconnectIdOverride ?? readReconnectSearchParam();
  const reconnectMode = Boolean(reconnectId);

  // Pull the existing account row when in reconnect mode so the title can
  // show the linkedin account name. We do NOT block render on the query —
  // if it hasn't resolved we fall back to the generic copy.
  const accountsQuery = useLinkedinAccounts({ enabled: reconnectMode });
  const reconnectingRow: LinkedinAccountRow | null = useMemo(() => {
    if (!reconnectId) return null;
    return accountsQuery.data?.accounts.find((acc) => acc.id === reconnectId) ?? null;
  }, [accountsQuery.data, reconnectId]);

  const [method, setMethod] = useState<Method>('extension');
  const effectiveMethod: Method = reconnectMode ? 'extension' : method;

  const [pairing, setPairing] = useState<PairingCodeState | null>(null);
  const [now, setNow] = useState(() => Date.now());
  const [copyState, setCopyState] = useState<CopyState>('idle');
  const onConnectedRef = useRef(onConnected);
  onConnectedRef.current = onConnected;
  const ackedRef = useRef(false);

  const detected = presence === 'present';
  const expiresIn = useMemo(() => {
    if (!pairing) return 0;
    return Math.max(0, Math.ceil((pairing.expiresAt - now) / 1000));
  }, [now, pairing]);
  const expired = pairing !== null && expiresIn === 0;
  const errorMessage =
    pairExtension.error instanceof ApiError
      ? pairExtension.error.message
      : pairExtension.error
        ? t('connect.extension.error')
        : null;

  useUserHubChannel('account.connected', (evt: UserHubServerEvent) => {
    if (!isExtensionAccountEvent(evt)) return;
    if (ackedRef.current) return;
    ackedRef.current = true;
    onConnectedRef.current();
  });

  useEffect(() => {
    if (!pairing || expired) return;
    const interval = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(interval);
  }, [expired, pairing]);

  const generatePairingCode = useCallback(async () => {
    try {
      const response = await pairExtension.mutateAsync();
      setPairing({ ...response, expiresAt: Date.now() + response.expiresIn * 1000 });
      setNow(Date.now());
      setCopyState('idle');
    } catch {
      // React Query owns the error state rendered below.
    }
  }, [pairExtension]);

  const copyCode = useCallback(async () => {
    if (!pairing) return;
    try {
      await navigator.clipboard.writeText(pairing.code);
      setCopyState('copied');
    } catch {
      setCopyState('failed');
    }
  }, [pairing]);

  const steps = [
    t('connect.extension.stepInstall'),
    t('connect.extension.stepGenerate'),
    t('connect.extension.stepPaste'),
  ];

  const pageTitle = reconnectMode
    ? t('connect.reconnect.title_template', { name: reconnectingRow?.name ?? '…' })
    : t('connect.pageTitle');
  const pageSubtitle = reconnectMode
    ? t('connect.reconnect.description_template', {
        name: reconnectingRow?.name ?? '…',
      })
    : t('connect.pageSubtitle');

  return (
    <main
      role="main"
      aria-label={t('connect.ariaMain')}
      data-ls-composite="connect-account-flow"
      data-method={effectiveMethod}
      data-reconnect={reconnectMode ? 'true' : undefined}
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--space-5)',
        padding: 'var(--space-6) var(--space-6) var(--space-8)',
        maxWidth: 720,
        marginInline: 'auto',
        width: '100%',
      }}
    >
      <header className="flex items-start gap-3 flex-wrap">
        <Button
          variant="ghost"
          size="sm"
          onClick={onBack}
          iconLeft={<ChevronLeft aria-hidden style={{ width: 14, height: 14 }} />}
          data-action="back"
        >
          {t('connect.back')}
        </Button>
        <div className="flex flex-1 flex-col gap-1">
          <h1 className="m-0 text-lg font-bold text-primary">{pageTitle}</h1>
          <p className="m-0 text-sm text-tertiary">{pageSubtitle}</p>
        </div>
      </header>

      {!reconnectMode ? (
        <div data-region="method-picker" className="flex flex-col gap-2">
          <span className="text-2xs font-semibold uppercase text-tertiary">
            {t('connect.method.label')}
          </span>
          <Segmented<Method>
            value={method}
            onValueChange={setMethod}
            aria-label={t('connect.method.label')}
            options={[
              { value: 'extension', label: t('connect.method.extension_label') },
              { value: 'cookie', label: t('connect.method.cookie_label') },
            ]}
          />
        </div>
      ) : null}

      {effectiveMethod === 'cookie' ? (
        <CookieConnectMethod
          onConnected={() => {
            onConnectedRef.current();
          }}
        />
      ) : (
        <Card padding="md" radius="2xl" data-region="connect-extension">
          <div className="flex flex-col gap-3">
            <div className="flex items-start gap-3">
              <span
                aria-hidden
                className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-accent-dim text-accent"
              >
                <Puzzle style={{ width: 18, height: 18 }} />
              </span>
              <div className="flex flex-col gap-1">
                <span className="text-sm font-bold text-primary">
                  {t('connect.extension.title')}
                </span>
                <span className="text-xs leading-relaxed text-secondary">
                  {t('connect.extension.body')}
                </span>
              </div>
            </div>
            <ol className="m-0 flex flex-col gap-2 border-t border-default pt-3 list-none p-0">
              {steps.map((label, idx) => (
                <li key={idx} className="flex items-center gap-2.5 text-xs text-secondary">
                  <span
                    aria-hidden
                    className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-accent-dim text-2xs font-bold text-accent"
                  >
                    {idx + 1}
                  </span>
                  <span>{label}</span>
                </li>
              ))}
            </ol>

            {detected ? (
              <Banner tone="success">{t('connect.extension.detected')}</Banner>
            ) : (
              <Banner tone="warning">{t('connect.extension.notDetected')}</Banner>
            )}
            {!HAS_PUBLISHED_EXTENSION && !detected ? (
              <Banner tone="warning">{t('connect.extension.unpublished')}</Banner>
            ) : null}

            {pairing ? (
              <div
                className="flex flex-col gap-2 rounded-md border border-default bg-bg-elevated p-3"
                data-testid="connect-extension-pairing-code"
              >
                <div className="flex items-center justify-between gap-3">
                  <span className="text-2xs font-semibold uppercase text-tertiary">
                    {t('connect.extension.codeLabel')}
                  </span>
                  <span
                    className={
                      'text-2xs font-semibold ' + (expired ? 'text-warning' : 'text-secondary')
                    }
                    role="status"
                    aria-live="polite"
                  >
                    {expired
                      ? t('connect.extension.expired')
                      : t('connect.extension.expiresIn', { seconds: expiresIn })}
                  </span>
                </div>
                <code className="select-all rounded bg-bg-surface px-3 py-2 text-center text-lg font-bold tracking-normal text-primary">
                  {pairing.code}
                </code>
                <p className="text-xs leading-relaxed text-secondary">
                  {t('connect.extension.pasteInstructions')}
                </p>
                <p className="text-2xs leading-relaxed text-tertiary">
                  {t('connect.extension.manualFallback')}
                </p>
                <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                  <Button
                    type="button"
                    variant="secondary"
                    size="sm"
                    fullWidth
                    onClick={() => {
                      void copyCode();
                    }}
                    disabled={!pairing}
                    iconLeft={<Copy aria-hidden style={{ width: 14, height: 14 }} />}
                    data-testid="connect-extension-copy-code"
                  >
                    {copyState === 'copied'
                      ? t('connect.extension.copySuccess')
                      : t('connect.extension.copyCta')}
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    fullWidth
                    loading={pairExtension.isPending}
                    onClick={() => {
                      void generatePairingCode();
                    }}
                    iconLeft={<RefreshCw aria-hidden style={{ width: 14, height: 14 }} />}
                    data-testid="connect-extension-regenerate"
                  >
                    {t('connect.extension.regenerateCta')}
                  </Button>
                </div>
                {copyState === 'failed' ? (
                  <p className="text-2xs leading-relaxed text-warning" role="status">
                    {t('connect.extension.copyFailed')}
                  </p>
                ) : null}
                <p className="text-2xs leading-relaxed text-tertiary">
                  {t('connect.extension.pendingGap')}
                </p>
              </div>
            ) : null}

            {errorMessage ? <Banner tone="error">{errorMessage}</Banner> : null}

            <Button
              type="button"
              variant="primary"
              size="md"
              fullWidth
              loading={pairExtension.isPending}
              onClick={() => {
                void generatePairingCode();
              }}
              data-testid="connect-extension-connect"
              data-action="generate-pairing-code"
            >
              {pairExtension.isPending
                ? t('connect.extension.generating')
                : pairing
                  ? t('connect.extension.generateNewCta')
                  : t('connect.extension.connectCta')}
            </Button>
            {!detected && HAS_PUBLISHED_EXTENSION ? (
              <Button
                asChild
                variant="ghost"
                size="sm"
                fullWidth
                data-testid="connect-extension-install"
              >
                <a
                  href={BAREL_EXTENSION_INSTALL_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center gap-2"
                >
                  <span>{t('connect.extension.installCta')}</span>
                  <ExternalLink aria-hidden style={{ width: 14, height: 14 }} />
                </a>
              </Button>
            ) : null}
          </div>
        </Card>
      )}

      <div className="flex flex-wrap gap-3 text-xs text-tertiary" aria-label="Trust assurances">
        <span className="inline-flex items-center gap-1">
          <Lock aria-hidden style={{ width: 12, height: 12 }} />
          {t('connect.extension.trustLocal')}
        </span>
        <span className="inline-flex items-center gap-1">
          <ShieldCheck aria-hidden style={{ width: 12, height: 12 }} />
          {t('connect.extension.trustNoPassword')}
        </span>
        <span className="inline-flex items-center gap-1">
          <RefreshCw aria-hidden style={{ width: 12, height: 12 }} />
          {t('connect.extension.trustAutoRefresh')}
        </span>
      </div>

      <p className="text-xs text-tertiary text-center m-0">{t('connect.helpFooter')}</p>
    </main>
  );
}

function isExtensionAccountEvent(evt: UserHubServerEvent): boolean {
  const payload = (evt as { payload?: unknown }).payload;
  if (!payload || typeof payload !== 'object') return false;
  const envelope = payload as Record<string, unknown>;
  if (envelope['source'] !== 'extension') return false;
  const account = envelope['account'];
  return Boolean(account && typeof account === 'object');
}
